import { Branch } from "./BranchModels";

export type WebsiteMediaType = "promo" | "before_after";

export enum EnumMediaType {
  Promo = 0,
  BeforeAfter = 1,
}

export const enumMediaTypeToKey = (
  value: EnumMediaType | string,
): WebsiteMediaType =>
  typeof value === "number"
    ? value === EnumMediaType.BeforeAfter
      ? "before_after"
      : "promo"
    : value === "before_after"
      ? "before_after"
      : "promo";

export const mediaTypeToEnum = (value: WebsiteMediaType): EnumMediaType =>
  value === "before_after" ? EnumMediaType.BeforeAfter : EnumMediaType.Promo;

export interface WebsiteMedia {
  id: string;
  organizationId: string;
  mediaType: EnumMediaType;
  title?: string | null;
  description?: string | null;
  imageUrl: string;
  beforeImageUrl?: string | null;
  afterImageUrl?: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

export interface WebsiteStat {
  id: string;
  organizationId: string;
  icon?: string | null;
  value: string;
  label: string;
  sortOrder: number;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

export interface Testimonial {
  id: string;
  organizationId: string;
  clientName: string;
  clientTitle?: string | null;
  clientPhotoUrl?: string | null;
  rating?: number | null;
  reviewText: string;
  sortOrder: number;
  isFeatured: boolean;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string | null;
  updatedBy?: string | null;
}

export interface Service {
  id: string;
  organizationId: string;
  name: string;
  description?: string | null;
  category?: string | null;
  durationMinutes?: number | null;
  price?: number | null;
  currency?: string | null;
  duration?: number | null;
  isActive: boolean;
  sortOrder: number;
  imageUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PublicOrganizationDto {
  name: string;
  slug: string;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  tagline?: string | null;
  description?: string | null;
  phone?: string | null;
  email?: string | null;
  googleMapsUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  linkedinUrl?: string | null;
  tiktokUrl?: string | null;
}

export interface PromoImageDto {
  imageUrl: string;
  title?: string | null;
  sortOrder: number;
}

export interface BeforeAfterDto {
  title?: string | null;
  beforeImageUrl?: string | null;
  afterImageUrl?: string | null;
  sortOrder: number;
}

export interface WebsiteStatDto {
  value: string;
  label: string;
  icon?: string | null;
}

export interface TestimonialDto {
  clientName: string;
  rating?: number | null;
  reviewText: string;
  clientPhotoUrl?: string | null;
}

export interface DoctorDto {
  id: string;
  name: string;
  title?: string | null;
  imageUrl?: string | null;
  bio?: string | null;
  sortOrder?: number;
}

export interface WebsiteMemberDto {
  userName: string;
  roleType?: number;
  roleName?: string | null;
  designation?: string | null;
  order?: number;
  qualification?: string | null;
  bio?: string | null;
  profilePictureUrl?: string | null;
  membershipStatus?: number;
  isActive?: boolean;
}

export interface BrandDto {
  id: string;
  name: string;
  logoUrl?: string | null;
}

export interface BlogPostDto {
  id: string;
  title: string;
  excerpt?: string | null;
  imageUrl?: string | null;
  link?: string | null;
}

export interface InfoCardDto {
  id: string;
  icon?: string | null;
  title?: string | null;
  text?: string | null;
}

export interface WebsiteSettings {
  id?: string;
  organizationId?: string;
  themeNumber?: number;
  brandColor?: string | null;
  appointmentImageUrl?: string | null;
  taglineImageUrl?: string | null;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateWebsiteSettingRequest {
  themeNumber?: number;
  brandColor?: string;
  appointmentImageUrl?: string;
  taglineImageUrl?: string;
  isActive?: boolean;
  appointmentImageFile?: File | null;
  taglineImageFile?: File | null;
}

export interface PublicWebsiteResponse {
  organization: PublicOrganizationDto;
  settings?: WebsiteSettings | null;
  promoImages: PromoImageDto[];
  beforeAfter: BeforeAfterDto[];
  stats: WebsiteStatDto[];
  testimonials: TestimonialDto[];
  services: Service[];
  branches: Branch[];
  members?: WebsiteMemberDto[] | null;
  doctors?: DoctorDto[] | null;
  brands?: BrandDto[] | null;
  blogPosts?: BlogPostDto[] | null;
  infoCards?: InfoCardDto[] | null;
}

export interface CreateWebsiteMediaRequest {
  organizationId: string;
  mediaType: WebsiteMediaType;
  title?: string;
  description?: string;
  imageFile?: File | null;
  beforeImageFile?: File | null;
  afterImageFile?: File | null;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateWebsiteMediaRequest {
  title?: string;
  description?: string;
  imageUrl?: string;
  beforeImageUrl?: string;
  afterImageUrl?: string;
  sortOrder?: number;
  isActive?: boolean;
  imageFile?: File | null;
  beforeImageFile?: File | null;
  afterImageFile?: File | null;
}

export interface CreateWebsiteStatRequest {
  organizationId: string;
  icon?: string;
  iconFile?: File | null;
  value: string;
  label: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface UpdateWebsiteStatRequest {
  icon?: string;
  value?: string;
  label?: string;
  sortOrder?: number;
  isActive?: boolean;
  iconFile?: File | null;
}

export interface CreateTestimonialRequest {
  organizationId: string;
  clientName: string;
  clientTitle?: string;
  clientPhotoUrl?: string;
  rating?: number;
  reviewText: string;
  sortOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;
}

export interface UpdateTestimonialRequest {
  clientName?: string;
  clientTitle?: string;
  clientPhotoUrl?: string;
  rating?: number;
  reviewText?: string;
  sortOrder?: number;
  isFeatured?: boolean;
  isActive?: boolean;
}